import React, { useEffect, useCallback, useState } from 'react';
import { ArrowContainer } from 'react-tiny-popover';

import PopoverWrapper from '../../_shared/components/Popover';
import {
  NotificationsArrow,
  NotificationsPopover
} from '../../_shared/constants/notifications';
import positionPopover from '../../_shared/utils/positionPopover';
import useAlertList from '../../_shared/hooks/alert/useAlertList';
import useTodoList from '../../_shared/hooks/todo/useTodoList';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import useAnalytics from '../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../_shared/constants/segmentConfig';
import NotificationsDropdown from './NotificationsDropdown';

import {
  StyledNotificationsContainer,
  StyledNotificationIcon,
  StyledNotificationBadge
} from './Styles';

const Notifications = ({
  isOmp = false,
  popOverChildRef,
  shouldShowTodoIcon
}) => {
  const {
    data: alerts,
    isError: isAlertsError,
    isStale: isAlertsStale,
    isLoading: isAlertsLoading,
    isFetching: isAlertsFetching,
    refetch: refetchAlerts,
    isRefetchError: refetchErrorAlerts,
    error: errorAlerts,
    isLoadingError: refetchErrorLoadingAlerts
  } = useAlertList();
  const {
    data: todos,
    isError: isTodosError,
    isStale: isTodosStale,
    isLoading: isTodosLoading,
    isFetching: isTodosFetching,
    refetch: refetchTodos,
    isRefetchError: refetchErrorTodos,
    error: errorTodos,
    isLoadingError: refetchErrorLoadingTodos
  } = useTodoList();
  const [visitedTodos, setVisitedTodos] = useState(false);
  const track = useAnalytics();
  const isTodoIconEnabled = isOmp && shouldShowTodoIcon;

  useEffect(() => {
    (async () => {
      if (
        !isTodosLoading &&
        isTodosStale &&
        !refetchErrorTodos &&
        errorTodos === null &&
        !refetchErrorLoadingTodos
      ) {
        await refetchTodos();
      }
      if (
        !isAlertsLoading &&
        isAlertsStale &&
        !refetchErrorAlerts &&
        errorAlerts === null &&
        !refetchErrorLoadingAlerts
      ) {
        await refetchAlerts();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasNotifications = alerts?.length > 0 || todos?.length > 0;

  const handleClickOutside = useCallback(() => {
    if (visitedTodos) {
      track(MixpanelConfig.names.selectTodo, {
        item_selected: false
      });
    }
    track(MixpanelConfig.names.selectAlert, {
      item_selected: false
    });
    setVisitedTodos(false);
  }, [visitedTodos, setVisitedTodos, track]);

  const popoverChildFn = useCallback(
    () => openPopOver => (
      <StyledNotificationsContainer
        data-testid="notification-icon"
        id="notification-icon"
        onClick={e => {
          if (isOmp) {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }
          openPopOver(isOpen => {
            if (!isOpen) {
              track(SegmentConfig.names.viewAlertsList, {});
              refetchAlerts();
            } else {
              handleClickOutside();
            }
            return !isOpen;
          });
        }}
        isOmp={isOmp}
      >
        <StyledNotificationIcon
          icon={isTodoIconEnabled ? 'TR5-Notif_todo' : 'TR5-Notif_bell'}
          isOmp={isOmp}
        />
        {hasNotifications && (
          <StyledNotificationBadge
            data-testid="notification-badge"
            isOmp={isOmp}
          />
        )}
      </StyledNotificationsContainer>
    ),
    [
      refetchAlerts,
      hasNotifications,
      handleClickOutside,
      isOmp,
      isTodoIconEnabled,
      track
    ]
  );

  const popoverContentFn = useCallback(
    () => (openPopOver, { childRect, popoverRect }) => (
      <ArrowContainer
        position={NotificationsArrow.position}
        childRect={childRect}
        popoverRect={popoverRect}
        arrowColor={NotificationsArrow.color}
        arrowSize={NotificationsArrow.size}
      >
        <NotificationsDropdown
          alerts={alerts}
          isAlertsError={isAlertsError}
          isAlertsFetching={isAlertsFetching}
          refetchAlerts={refetchAlerts}
          todos={todos}
          isTodosError={isTodosError}
          isTodosFetching={isTodosFetching}
          refetchTodos={refetchTodos}
          openPopOver={openPopOver}
          visitedTodos={visitedTodos}
          setVisitedTodos={setVisitedTodos}
          isOmp={isOmp}
        />
      </ArrowContainer>
    ),
    [
      alerts,
      refetchAlerts,
      isAlertsError,
      isAlertsFetching,
      todos,
      refetchTodos,
      isTodosError,
      isTodosFetching,
      visitedTodos,
      setVisitedTodos,
      isOmp
    ]
  );

  return (
    <PopoverWrapper
      align={NotificationsPopover.align}
      position={NotificationsPopover.position}
      contentLocation={positionPopover(-40, -185)}
      renderContent={popoverContentFn()}
      renderChild={popoverChildFn()}
      handleClickOutside={handleClickOutside}
      popOverChildRef={popOverChildRef}
      validateClickOutside={isOmp}
    />
  );
};

export default Notifications;
